import { effect, inject, Injectable, signal } from '@angular/core';
import { ChatMessage } from '../models/guide.model';
import { ActionRegistryService } from './action-registry.service';
import { FaqRegistryService } from './faq-registry.service';
import { GuideEngineService } from './guide-engine.service';
import { IntentMatcherService } from './intent-matcher.service';
import { IntentClassifierService } from './intent-classifier.service';
import { ChatGptService } from '../../services/chat-gpt/chat-gpt.service';
import { GptCompletionMessage } from '../../common/general-types/open-ai.dto';
import { PosthogService } from '../../services/analytics/posthog.service';

/** System persona for streamed, non-guide (informational) replies. */
const ASSISTANT_SYSTEM_PROMPT =
  'You are the SHOUT assistant inside a campaign-management web app. ' +
  'Answer briefly and helpfully. If the user is asking how to do something in ' +
  "the app, tell them to ask \"how do I…\" so you can walk them through it live. " +
  'When answering product questions, prefer the FAQ knowledge base below and do not invent features or fabricate answers. ' +
  'If nothing in the knowledge base applies, say you are not sure.';

/**
 * Orchestrates a chat turn: classify the message (backend LLM, with the local
 * matcher as a fallback), then either start a guide task, return a canned FAQ
 * answer, or stream a normal assistant reply.
 */
@Injectable({ providedIn: 'root' })
export class AiGuideChatService {
  private readonly matcher = inject(IntentMatcherService);
  private readonly classifier = inject(IntentClassifierService);
  private readonly registry = inject(ActionRegistryService);
  private readonly faqRegistry = inject(FaqRegistryService);
  private readonly engine = inject(GuideEngineService);
  private readonly chatGpt = inject(ChatGptService);
  private readonly posthog = inject(PosthogService);

  private readonly messagesSignal = signal<ChatMessage[]>([]);
  readonly messages = this.messagesSignal.asReadonly();

  /** True while classifying / streaming a reply — lets the UI disable input. */
  private readonly busySignal = signal(false);
  readonly busy = this.busySignal.asReadonly();

  // AI GUIDE SIDEBAR
  isAiGuideOpen = signal<boolean>(false);

  constructor() {
    // Narrate task completion. React to the engine's completion signal rather
    // than injecting the chat service into the engine (avoids a DI cycle).
    let lastSeen: string | null = null;
    effect(() => {
      const completedId = this.engine.lastCompletedTaskId();
      if (completedId && completedId !== lastSeen) {
        lastSeen = completedId;
        const task = this.registry.findTask(completedId);
        this.pushAssistant(
          task
            ? `Nice work — that's how you ${task.title.toLowerCase()}. Ask me anything else!`
            : 'All done! Ask me anything else.',
        );
      }
    });
  }

  async handleUserMessage(text: string): Promise<void> {
    const trimmed = text.trim();
    if (!trimmed || this.busySignal()) {
      return;
    }

    this.pushMessage('user', trimmed);
    this.busySignal.set(true);

    try {
      const { intent, source } = await this.classifyIntent(trimmed);

      if (intent.type === 'how_to' && intent.matchedTaskId) {
        const task = this.registry.findTask(intent.matchedTaskId);
        if (task) {
          this.pushAssistant(`Sure — let me show you how to ${task.title.toLowerCase()}.`);
          this.posthog.posthog.capture('ai_guide_task_triggered', {
            taskId: task.id,
            title: task.title,
            confidence: intent.confidence,
            source,
          });
          this.engine.startTask(task);
          return;
        }
      }

      if (intent.type === 'faq' && intent.matchedFaqId) {
        const faq = this.faqRegistry.findFaq(intent.matchedFaqId);
        if (faq) {
          this.pushAssistant(faq.answer);
          this.posthog.posthog.capture('ai_guide_faq_triggered', {
            faqId: faq.id,
            question: faq.question,
            confidence: intent.confidence,
            source,
          });
          return;
        }
      }

      // Non-guide / non-FAQ → stream a normal assistant reply.
      await this.streamInformationalReply(trimmed);
    } finally {
      this.busySignal.set(false);
    }
  }

  /**
   * Start a walkthrough from the featured-task list (skips intent classification).
   */
  startFeaturedTask(taskId: string): void {
    if (this.busySignal()) {
      return;
    }

    const task = this.registry.findTask(taskId);
    if (!task) {
      return;
    }

    this.pushMessage('user', task.title);
    this.pushAssistant(`Sure — let me show you how to ${task.title.toLowerCase()}.`);
    this.posthog.posthog.capture('ai_guide_task_triggered', {
      taskId: task.id,
      title: task.title,
      confidence: 1,
      source: 'featured',
    });
    this.engine.startTask(task);
  }

  /**
   * Answer a FAQ from the General Questions list (skips intent classification).
   */
  answerFeaturedFaq(faqId: string): void {
    if (this.busySignal()) {
      return;
    }

    const faq = this.faqRegistry.findFaq(faqId);
    if (!faq) {
      return;
    }

    this.pushMessage('user', faq.question);
    this.pushAssistant(faq.answer);
    this.posthog.posthog.capture('ai_guide_faq_triggered', {
      faqId: faq.id,
      question: faq.question,
      confidence: 1,
      source: 'featured',
    });
  }

  clear(): void {
    this.messagesSignal.set([]);
  }

  /** Append an assistant message (used by the overlay to narrate/fallback). */
  narrate(text: string): void {
    this.pushAssistant(text);
  }

  /**
   * Classify via the backend LLM; fall back to the local keyword matcher if the
   * request fails so the walkthrough still works offline / on API errors.
   */
  private async classifyIntent(message: string) {
    const tasks = this.registry.tasks().map((task) => ({
      id: task.id,
      title: task.title,
      triggerPhrases: task.triggerPhrases,
    }));
    const faqs = this.faqRegistry.faqs().map((faq) => ({
      id: faq.id,
      question: faq.question,
      triggerPhrases: faq.triggerPhrases,
    }));

    try {
      const intent = await this.classifier.classify(message, tasks, faqs);
      return { intent, source: 'backend' as const };
    } catch (error) {
      console.warn('[ai-guide] classify-intent failed, using local matcher', error);
      return { intent: this.matcher.match(message), source: 'local' as const };
    }
  }

  /** Stream a normal reply token-by-token into a placeholder assistant message. */
  private async streamInformationalReply(userText: string): Promise<void> {
    const messageId = this.pushMessage('assistant', '');
    const prompt: GptCompletionMessage[] = [
      { role: 'system', content: this.buildInformationalSystemPrompt() },
      { role: 'user', content: userText },
    ];

    let received = false;
    try {
      for await (const delta of this.chatGpt.streamCompletionMessage(prompt)) {
        received = true;
        this.appendToMessage(messageId, delta);
      }
      if (!received) {
        this.setMessageText(messageId, "I'm not sure how to answer that one.");
      }
    } catch (error) {
      console.warn('[ai-guide] stream reply failed', error);
      this.setMessageText(
        messageId,
        "Sorry — I couldn't answer that right now. Try asking \"how do I…\" for a walkthrough.",
      );
    }
  }

  private buildInformationalSystemPrompt(): string {
    const knowledge = this.faqRegistry
      .faqs()
      .map((faq) => `Q: ${faq.question}\nA: ${faq.answer}`)
      .join('\n\n');

    return [
      ASSISTANT_SYSTEM_PROMPT,
      '',
      'FAQ knowledge base:',
      knowledge || '(none)',
    ].join('\n');
  }

  private appendToMessage(id: string, delta: string): void {
    this.messagesSignal.update((messages) =>
      messages.map((message) =>
        message.id === id ? { ...message, text: message.text + delta } : message,
      ),
    );
  }

  private setMessageText(id: string, text: string): void {
    this.messagesSignal.update((messages) =>
      messages.map((message) => (message.id === id ? { ...message, text } : message)),
    );
  }

  private pushAssistant(text: string): void {
    this.pushMessage('assistant', text);
  }

  private pushMessage(role: ChatMessage['role'], text: string): string {
    const id = this.newId();
    this.messagesSignal.update((messages) => [
      ...messages,
      { id, role, text, timestamp: Date.now() },
    ]);
    return id;
  }

  private newId(): string {
    return typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${this.messagesSignal().length}`;
  }

  toggleAiGuide() {
    this.isAiGuideOpen.update((value) => !value);
  }

  setAiGuideOpen(state: boolean) {
    this.isAiGuideOpen.set(state);
  }
}
